import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, Printer, Download, X, Loader2, MessageSquareWarning, Award } from "lucide-react";
import client from "@/lib/api";
import { toast } from "sonner";

const KINDS = [["either", "COMPLAINT OR COMMENDATION", "#E0B23C"], ["complaint", "COMPLAINT", "#DC2626"], ["commendation", "COMMENDATION", "#16A34A"]];

// Supervisor or MDT user generates a QR that opens the public portal pre-filled with officer/unit/CAD#.
export function FeedbackQrCard({ myUnit, activeCc, isSupervisor, compact = false }) {
  const [kind, setKind] = useState("either");
  const [cc, setCc] = useState(activeCc || "");
  const [officer, setOfficer] = useState("");
  const [badge, setBadge] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);
  const [mine, setMine] = useState([]);
  useEffect(() => { setCc(activeCc || ""); }, [activeCc]);
  const load = () => client.get("/feedback-qr", { params: { all: !!isSupervisor } }).then((r) => setMine(r.data.codes)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/feedback-qr", { kind, incident_cc: cc || null, unit: myUnit || null, officer_name: officer || null, badge: badge || null, label: label || null, base_url: window.location.origin });
      setQr(data); load();
      toast.success("QR code ready — show it on screen or print it");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create QR"); }
    finally { setBusy(false); }
  };

  const print = () => {
    const w = window.open("", "_blank", "width=480,height=640");
    const svg = document.getElementById(`fqr-${qr.code}`)?.outerHTML || "";
    w.document.write(`<html><body style="font-family:Arial;text-align:center;padding:30px"><h2 style="margin:0">MATCOM PUBLIC SAFETY</h2><div style="color:#555;font-size:13px;margin-bottom:16px">Scan to file a ${qr.kind === "either" ? "complaint or commendation" : qr.kind}</div>${svg}<div style="margin-top:14px;font-size:14px"><b>${qr.officer_name}</b>${qr.badge ? ` · Badge ${qr.badge}` : ""}${qr.unit ? ` · Unit ${qr.unit}` : ""}</div>${qr.incident_cc ? `<div style="font-size:12px;color:#555">CAD# ${qr.incident_cc}</div>` : ""}<div style="font-size:11px;color:#777;margin-top:10px;word-break:break-all">${qr.url}</div><div style="font-size:10px;color:#999;margin-top:18px">Anonymous submissions accepted · Not for emergencies — call 911</div></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const kc = KINDS.find((k) => k[0] === (qr?.kind || kind))[2];
  return (
    <div data-testid="feedback-qr-card" className="border border-[#2C2D35] bg-[#141518] p-3">
      <div className="flex items-center gap-2 mb-2">
        <QrCode size={15} className="text-[#E0B23C]" />
        <span className="font-head text-xs font-bold text-white tracking-wider">PUBLIC FEEDBACK QR</span>
        <span className="text-[10px] font-mono text-[#9CA3AF] ml-auto">complaint / commendation</span>
      </div>
      {!qr ? (
        <div className="space-y-2">
          <div className="flex gap-1 flex-wrap">
            {KINDS.map(([k, l, c]) => (
              <button key={k} data-testid={`fqr-kind-${k}`} onClick={() => setKind(k)} className="px-2 py-1 text-[10px] font-bold border" style={{ borderColor: c, color: kind === k ? "#000" : c, background: kind === k ? c : "transparent" }}>{l}</button>
            ))}
          </div>
          <div className={`grid gap-2 ${compact ? "grid-cols-1" : "grid-cols-2"}`}>
            <input data-testid="fqr-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="CAD# (optional)" className="bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono text-white outline-none" />
            <input data-testid="fqr-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (traffic stop, event, card…)" className="bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono text-white outline-none" />
            {isSupervisor && <input data-testid="fqr-officer" value={officer} onChange={(e) => setOfficer(e.target.value)} placeholder="Officer name (default: you)" className="bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono text-white outline-none" />}
            {isSupervisor && <input data-testid="fqr-badge" value={badge} onChange={(e) => setBadge(e.target.value)} placeholder="Badge #" className="bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono text-white outline-none" />}
          </div>
          <button data-testid="fqr-generate" disabled={busy} onClick={gen} className="w-full py-2 font-head text-xs font-bold text-black flex items-center justify-center gap-2" style={{ background: kc }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <QrCode size={13} />} GENERATE QR CODE
          </button>
        </div>
      ) : (
        <div className="flex gap-3 items-start">
          <div className="bg-white p-2 shrink-0" data-testid="fqr-image"><QRCodeSVG id={`fqr-${qr.code}`} value={qr.url} size={compact ? 140 : 176} level="M" /></div>
          <div className="min-w-0 flex-1 text-xs font-mono">
            <div className="font-bold" style={{ color: kc }}>{KINDS.find((k) => k[0] === qr.kind)[1]}</div>
            <div className="text-white mt-1">{qr.officer_name}{qr.badge ? ` · #${qr.badge}` : ""}{qr.unit ? ` · ${qr.unit}` : ""}</div>
            {qr.incident_cc && <div className="text-[#9CA3AF]">CAD# {qr.incident_cc}</div>}
            <div data-testid="fqr-url" className="text-[10px] text-[#60A5FA] break-all mt-1">{qr.url}</div>
            <div className="flex gap-1 mt-2 flex-wrap">
              <button data-testid="fqr-print" onClick={print} className="px-2 py-1 border border-[#2C2D35] text-[10px] font-bold text-white flex items-center gap-1"><Printer size={11} /> PRINT</button>
              <a data-testid="fqr-png" href={`${process.env.REACT_APP_BACKEND_URL}/api/feedback-qr/${qr.code}.png?size=12`} target="_blank" rel="noreferrer" className="px-2 py-1 border border-[#2C2D35] text-[10px] font-bold text-white flex items-center gap-1"><Download size={11} /> PNG</a>
              <button data-testid="fqr-new" onClick={() => setQr(null)} className="px-2 py-1 border border-[#2C2D35] text-[10px] font-bold text-[#9CA3AF] flex items-center gap-1"><X size={11} /> NEW</button>
            </div>
          </div>
        </div>
      )}
      {mine.length > 0 && (
        <div className="mt-3 border-t border-[#2C2D35] pt-2" data-testid="fqr-history">
          <div className="text-[10px] font-mono text-[#9CA3AF] uppercase mb-1">{isSupervisor ? "All codes" : "My codes"} ({mine.length})</div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {mine.slice(0, 20).map((c) => (
              <div key={c.code} data-testid={`fqr-row-${c.code}`} className="flex items-center gap-2 text-[10px] font-mono">
                {c.kind === "complaint" ? <MessageSquareWarning size={11} className="text-[#DC2626]" /> : c.kind === "commendation" ? <Award size={11} className="text-[#16A34A]" /> : <QrCode size={11} className="text-[#E0B23C]" />}
                <span className="text-white truncate">{c.officer_name}{c.unit ? ` · ${c.unit}` : ""}{c.incident_cc ? ` · ${c.incident_cc}` : ""}{c.label ? ` · ${c.label}` : ""}</span>
                <span className="ml-auto text-[#9CA3AF] shrink-0">{c.scans} scan{c.scans === 1 ? "" : "s"} · {c.submission_count} filed{c.active ? "" : " · OFF"}</span>
                <button onClick={() => setQr(c)} className="text-[#60A5FA] shrink-0">SHOW</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
